import { useMemo } from "react";
import { useApi } from "@platform/api";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { getPlanBuilderData } from "../api/client";

import type { PlanBuilderResponse, SDLCoverageData } from "../api/types";
import type { CoverageGroup, DocumentCardProps } from "../types";

const PLAN_BUILDER_DATA_QUERY_KEY = "planBuilderData";

export const getPlanBuilderDataCacheKey = (rfpId: string): string[] => [
  PLAN_BUILDER_DATA_QUERY_KEY,
  rfpId,
];

export interface PlanBuilderData {
  isLoading: boolean;
  isError: boolean;
  coverageGroups: CoverageGroup[];
  documentsById: Record<string, DocumentCardProps>;
  completedCount: number;
  totalCount: number;
  sdlCoverage: SDLCoverageData | null;
}

export function usePlanBuilderData(
  rfpId: string,
  options?: { enabled?: boolean },
): PlanBuilderData {
  const axiosInstance = useApi();

  const query: UseQueryResult<PlanBuilderResponse, Error> = useQuery({
    queryKey: getPlanBuilderDataCacheKey(rfpId),
    queryFn: () => getPlanBuilderData(rfpId, axiosInstance),
    enabled: !!rfpId && (options?.enabled ?? true),
  });

  const coverageGroups = useMemo(() => {
    if (!query.data) return [];
    return [
      ...query.data.currentCoverages,
      ...query.data.newCoverages,
      ...query.data.otherCoverages,
    ];
  }, [query.data]);

  const documentsById = useMemo(
    () =>
      coverageGroups.reduce<Record<string, DocumentCardProps>>((acc, group) => {
        group.documents.forEach((doc) => {
          acc[doc.id] = doc;
        });
        return acc;
      }, {}),
    [coverageGroups],
  );

  const completedCount = query.data?.completedCount ?? 0;
  const totalCount = query.data?.totalCount ?? 0;

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    coverageGroups,
    documentsById,
    completedCount,
    totalCount,
    sdlCoverage: query.data?.sdlCoverage ?? null,
  };
}
